import { PaymentProvider } from "@greenflagdate/shared";
import { chakra } from "@chakra-ui/react";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { isCreditCard, isPostalCode } from "validator";
import {
  AcceptResponse,
  dispatchCardData,
  loadAuthorizeAccept,
} from "../../lib/payments/authorizenet/acceptJs";
import { translateAnetCode } from "../../lib/payments/authorizenet/errorMap";
import {
  CardFieldsHandle,
  CardFieldsProps,
  GENERIC_PAYMENT_ERROR,
} from "../../lib/payments/types";
import { cardInputProps, digitsOnly } from "./cardInput";
import { CardFormErrors, CardFormLayout } from "./CardFormLayout";

/** `1234 5678 9012 3456` — groups of four, at most nineteen characters. */
const formatCardNumber = (raw: string): string => {
  const digits = raw.replace(/ /g, "");
  const groups: string[] = [];
  for (let i = 0; i < digits.length; i += 4) {
    groups.push(digits.slice(i, i + 4));
  }
  return groups.join(" ").substring(0, 19);
};

/** `MM / YY`, with a lone `2`–`9` read as a month and padded to `02`–`09`. */
const formatExpDate = (raw: string): string => {
  const digits = raw.replace(/[ /]/g, "");
  let month = digits.substring(0, 2);
  const year = digits.substring(2, 4);
  if (month.length === 1 && month > "1") {
    month = "0" + month;
  }
  return month.length === 2 ? `${month} / ${year}` : month;
};

/**
 * Authorize.net card entry.
 *
 * Accept.js tokenizes card data the page has already collected, so this
 * adapter supplies text inputs of its own to `CardFormLayout` and posts what
 * they hold to `Accept.dispatchData`. The Stripe fields render the same layout
 * around inputs Stripe hosts and never see the number — which is why the card
 * UI belongs to the adapter rather than to PaymentForm.
 */
export const AuthorizeNetCardFields = forwardRef<
  CardFieldsHandle,
  CardFieldsProps
>(({ error, onCancel }, ref) => {
  const [cardNumber, setCardNumber] = useState("");
  const [expDate, setExpDate] = useState("");
  const [cvc, setCvc] = useState("");
  const [name, setName] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [errors, setErrors] = useState<CardFormErrors>();

  useEffect(() => {
    loadAuthorizeAccept().catch(() => {
      /* noop — tokenize() blocks on readiness and reports the failure there */
    });
  }, []);

  useImperativeHandle(ref, () => ({
    async tokenize() {
      const finalCardNumber = cardNumber.replace(/ /g, "");
      const fieldErrors: CardFormErrors = {};

      if (!finalCardNumber) {
        fieldErrors.cardNumber = "Please enter your credit card number";
      } else if (!isCreditCard(finalCardNumber)) {
        fieldErrors.cardNumber = "Card number invalid";
      }

      if (!cvc) {
        fieldErrors.cvc = "Please enter your credit card CVC";
      } else if (!/^[0-9]{3,4}$/.test(cvc)) {
        fieldErrors.cvc = "CVC invalid";
      }
      if (!name.length) {
        fieldErrors.name = "Name is required";
      }

      const [expMonth, expYear] = expDate.split("/").map((v) => v.trim());
      if (expMonth.length !== 2 || expYear?.length !== 2) {
        fieldErrors.expDate = "Expiration date invalid";
      }

      if (!postalCode.length) {
        fieldErrors.postalCode = "Zip code is required";
      } else if (!isPostalCode(postalCode, "US")) {
        fieldErrors.postalCode = "Please double check your zip code";
      }

      setErrors(fieldErrors);
      if (Object.keys(fieldErrors).length) {
        return null;
      }

      let aNetResult: AcceptResponse;
      try {
        aNetResult = await dispatchCardData({
          authData: {
            clientKey: import.meta.env.VITE_ANET_PUBLIC_CLIENT_KEY!,
            apiLoginID: import.meta.env.VITE_ANET_API_LOGIN_ID!,
          },
          cardData: {
            cardNumber: finalCardNumber,
            month: expMonth,
            year: expYear,
            cardCode: cvc,
          },
        });
      } catch {
        setErrors({ authorize: "Unable to connect to payment processor" });
        return null;
      }

      if (aNetResult.messages.resultCode.toLowerCase() === "error") {
        setErrors({
          authorize:
            translateAnetCode(aNetResult.messages.message[0]?.code) ??
            GENERIC_PAYMENT_ERROR,
        });
        return null;
      }

      if (!aNetResult.opaqueData) {
        setErrors({ authorize: "Something went wrong. Please try again." });
        return null;
      }

      return {
        token: {
          provider: PaymentProvider.AuthorizeNet,
          token: aNetResult.opaqueData.dataValue,
          // Accept.js splits its token in two. The descriptor means nothing to
          // any other processor, so it rides in `meta` rather than widening
          // the shared token shape.
          meta: { dataDescriptor: aNetResult.opaqueData.dataDescriptor },
        },
        postalCode,
      };
    },
  }));

  return (
    <CardFormLayout
      onCancel={onCancel}
      errors={error ? { ...errors, authorize: error } : errors}
      name={name}
      onNameChange={setName}
      postalCode={postalCode}
      onPostalCodeChange={setPostalCode}
      cardNumber={
        <chakra.input
          type="text"
          placeholder="1234 1234 1234 1234"
          autoComplete="cc-number"
          value={cardNumber}
          {...cardInputProps}
          pr="160px"
          onKeyDown={digitsOnly}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setCardNumber(formatCardNumber(e.target.value))
          }
        />
      }
      expiry={
        <chakra.input
          type="text"
          placeholder="MM / YY"
          autoComplete="cc-exp"
          value={expDate}
          {...cardInputProps}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            // Backspacing into the separator drops it along with the month's
            // last digit, so the user is not left deleting " / " a key at a
            // time.
            if (
              (e.key === "Backspace" || e.key === "Delete") &&
              /^[0-9]{2} \/ [0-9]?$/.test(expDate)
            ) {
              e.preventDefault();
              setExpDate(expDate.substring(0, 2));
            }
            digitsOnly(e);
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setExpDate(formatExpDate(e.target.value))
          }
        />
      }
      cvc={
        <chakra.input
          type="text"
          placeholder="CVC"
          autoComplete="cc-csc"
          value={cvc}
          {...cardInputProps}
          onKeyDown={digitsOnly}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setCvc(e.target.value.substring(0, 4))
          }
        />
      }
    />
  );
});
